import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissionStore } from '@/stores/permissions'
import { useIntegrationStore } from '@/stores/integrations'
import { loadModules } from './module-loader'
import { isProductivityVisible } from '@/core/consoleCommands'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { publicRoutes as mapPublicRoutes } from '@/modules/map/routes'
import { publicRoutes as qrPublicRoutes } from '@/modules/qr-codes/routes'
import { publicRoutes as formPublicRoutes } from '@/modules/tickets/routes'

// Load module routes
const { routes: moduleRoutes } = loadModules()

const baseRoutes: RouteRecordRaw[] = [
    // Public routes MUST be before the root '/' route — its nested catch-all
    // (/:pathMatch(.*)*) will swallow any path that isn't matched first.
    ...mapPublicRoutes,
    ...qrPublicRoutes,
    ...formPublicRoutes,
    // Redirect /it-requests/* → /tickets/* so external links (emails, shared URLs, Teams cards) work
    {
        path: '/it-requests/:pathMatch(.*)*',
        redirect: (to) => ({
            path: `/tickets/${(to.params.pathMatch as string[]).join('/')}`,
            query: to.query,
        }),
    },
    {
        path: '/',
        component: DefaultLayout,
        meta: { requiresAuth: true, requiresOrganization: true },
        children: [
            {
                path: '',
                redirect: () => {
                    return '/dashboard'
                },
            },
            {
                path: 'profile',
                name: 'Profile',
                component: () => import('@/views/Profile.vue'),
            },
            // Module routes are added here
            ...moduleRoutes,
            {
                path: '/:pathMatch(.*)*',
                name: 'NotFound',
                component: () => import('@/views/NotFound.vue'),
            },
        ],
    },
    {
        path: '/login',
        name: 'Login',
        component: () => import('@/views/Login.vue'),
        meta: { requiresAuth: false },
    },
    {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: () => import('@/views/ForgotPassword.vue'),
        meta: { requiresAuth: false },
    },
    {
        path: '/auth/set-password',
        name: 'SetPassword',
        component: () => import('@/views/SetPassword.vue'),
        meta: { requiresAuth: false },
    },
    {
        path: '/auth/callback',
        name: 'AuthCallback',
        component: () => import('@/views/AuthCallback.vue'),
        meta: { requiresAuth: false },
    },
    {
        path: '/no-organization',
        name: 'NoOrganization',
        component: () => import('@/views/NoOrganization.vue'),
        meta: { requiresAuth: true },
    },
    {
        path: '/privacy',
        name: 'PrivacyPolicy',
        component: () => import('@/views/PrivacyPolicy.vue'),
        meta: { requiresAuth: false },
    },
]

const router = createRouter({
    history: createWebHistory(),
    routes: baseRoutes,
})

// Navigation guard for auth and RBAC
router.beforeEach(async (to, _from, next) => {
    // Skip auth check for public routes
    const publicRoutes = ['Login', 'ForgotPassword', 'SetPassword', 'AuthCallback', 'PrivacyPolicy', 'public-map', 'public-map-detail', 'qr-status', 'qr-verify', 'public-form']
    if (publicRoutes.includes(to.name as string)) {
        return next()
    }

    // Check if route requires authentication
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

    if (requiresAuth) {
        const authStore = useAuthStore()

        // Initialize auth store if not already done
        if (!authStore.isInitialized) {
            await authStore.initialize()
        }

        // Load integrations once after auth is ready
        const integrationStore = useIntegrationStore()
        if (!integrationStore.isLoaded) {
            integrationStore.loadIntegrations()
        }

        // If not authenticated, redirect to login
        if (!authStore.isAuthenticated) {
            return next({ name: 'Login', query: { redirect: to.fullPath } })
        }

        // Check if route requires organization association
        const requiresOrganization = to.matched.some(record => record.meta.requiresOrganization)
        if (requiresOrganization && !authStore.hasOrganization) {
            // User is authenticated but has no organization - redirect to error page
            if (to.name !== 'NoOrganization') {
                return next({ name: 'NoOrganization' })
            }
        }
    }

    // Check DevTools console visibility override for productivity module
    if (to.path.startsWith('/productivity') && !isProductivityVisible.value) {
        console.warn(`Access denied to ${to.path}. Productivity module is hidden via console storage key.`)
        return next({ name: 'NotFound' })
    }

    // Check route-level module permission requirements
    const moduleKey = to.meta.moduleKey as string | undefined

    if (moduleKey) {
        const permissionStore = usePermissionStore()

        // Ensure permissions are loaded before checking
        if (!permissionStore.isLoaded) {
            await permissionStore.load()
        }

        if (!permissionStore.canView(moduleKey)) {
            // User doesn't have required permission - redirect to NotFound
            console.warn(`Access denied to ${to.path}. Required moduleKey: ${moduleKey}`)
            return next({ name: 'NotFound' })
        }
    }

    next()
})

// Global error handler for dynamic import failures (e.g. after a new deployment)
router.onError((error, to) => {
    if (error.message.includes('Failed to fetch dynamically imported module') || error.message.includes('Importing a module script failed')) {
        console.warn('Dynamic import failed, reloading page to fetch new chunks...', error)
        // Set a flag to prevent infinite reload loops if the file is actually missing on the server
        const reloadCount = parseInt(sessionStorage.getItem('rws:chunkLoadRetry') || '0', 10)

        if (reloadCount < 2) {
            sessionStorage.setItem('rws:chunkLoadRetry', (reloadCount + 1).toString())
            window.location.href = to.fullPath
        } else {
            console.error('Failed to load chunks after multiple retries. Please clear your cache.')
            // Reset the counter so they can try again later
            sessionStorage.removeItem('rws:chunkLoadRetry')
        }
    } else {
        console.error('Router navigation error:', error)
    }
})

// Dynamic page title (ADA/SEO) + clean up chunk retry flag
router.afterEach((to) => {
    sessionStorage.removeItem('rws:chunkLoadRetry')
    const title = (to.meta?.title as string)
        || (to.name as string)?.replace(/([A-Z])/g, ' $1').trim()
        || 'Page'
    document.title = `${title} — RedIS Web Services`
})

export default router
